import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { panelTiltLeave, panelTiltMove } from './t05CardTilt';
import { t05StackIconSrc, type T05ProductStackSection } from './t05ProductStackData';
import { T05StackChatPanel } from './T05StackChatPanel';
import { T05_STACK_HEADER_H } from './useProductStack';

export interface T05ProductStackProps {
  sections: readonly T05ProductStackSection[];
  sectionRef: React.RefObject<HTMLElement | null>;
  frameRef: React.RefObject<HTMLDivElement | null>;
  activeIndex: number;
  navOffset: number;
  isDesktop: boolean;
  scrollToSection: (index: number) => void;
}

export const T05ProductStack: React.FC<T05ProductStackProps> = ({
  sections,
  sectionRef,
  frameRef,
  activeIndex,
  navOffset,
  isDesktop,
  scrollToSection,
}) => {
  return (
    <section
      ref={sectionRef}
      id="produkte"
      className="t05-product-stack t05-container"
      data-section-motion="from-right"
      aria-labelledby="t05-stack-heading"
    >
      <div className="t05-product-stack__layout">
        <div className="t05-product-stack__sidebar-wrap" data-stack-sidebar-wrap>
          <div
            data-stack-sidebar
            className="t05-product-stack__sidebar"
            style={isDesktop ? { top: `${navOffset}px` } : undefined}
          >
            <h2 id="t05-stack-heading" className="t05-product-stack__headline">
              Orbis — alles in einer Oberfläche.
            </h2>
            <nav className="t05-product-stack__nav" aria-label="Produktbereiche">
              {sections.map((section, index) => {
                const isActive = activeIndex === index;
                return (
                  <button
                    key={section.id}
                    type="button"
                    className={`t05-product-stack__nav-btn${isActive ? ' t05-product-stack__nav-btn--active' : ''}`}
                    onClick={() => scrollToSection(index)}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={`${section.num} ${section.title}`}
                    style={
                      isActive
                        ? ({
                            '--stack-nav-accent': section.accent,
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {section.num}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div ref={frameRef} className="t05-product-stack__frame" data-stack-frame>
          <div className="t05-product-stack__ambient" aria-hidden />
          {sections.map((section, index) => {
            const iconSrc = t05StackIconSrc(section.iconFile);

            return (
              <article
                key={section.id}
                data-stack-card
                className="t05-product-stack__card"
                style={{
                  '--stack-i': index,
                  '--stack-accent': section.accent,
                  ...(isDesktop
                    ? { top: `${navOffset + index * T05_STACK_HEADER_H}px` }
                    : {}),
                } as React.CSSProperties}
              >
                <div className="t05-product-stack__header">
                  <img
                    src={iconSrc}
                    alt=""
                    className="t05-product-stack__header-icon"
                    width={32}
                    height={32}
                    loading="lazy"
                    decoding="async"
                  />
                  <h3 className="t05-product-stack__title">
                    {section.title}
                  </h3>
                  <span className="t05-product-stack__header-num" aria-hidden>
                    {section.num}
                  </span>
                </div>

                <div className="t05-product-stack__body">
                  <div className="t05-product-stack__body-inner">
                    <div className="t05-product-stack__copy">
                      <p className="t05-product-stack__desc">{section.description}</p>
                      <p className="t05-product-stack__detail">{section.detail}</p>
                      <ul className="t05-product-stack__highlights">
                        {section.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      <Link
                        to={section.href}
                        className="t05-product-stack__cta"
                        style={{ '--stack-cta-accent': section.accent } as React.CSSProperties}
                      >
                        Mehr erfahren
                        <ArrowRight size={16} aria-hidden />
                      </Link>
                    </div>

                    <div className="t05-product-stack__visual">
                      <div
                        className={[
                          't05-product-stack__panel',
                          section.panelVariant === 'chat' && 't05-product-stack__panel--chat',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseMove={panelTiltMove}
                        onMouseLeave={panelTiltLeave}
                      >
                        {section.panelVariant === 'chat' && section.chatMock ? (
                          <T05StackChatPanel panelTitle={section.panelTitle} chat={section.chatMock} />
                        ) : (
                          <>
                            <div className="t05-product-stack__panel-dots" aria-hidden>
                              <span />
                              <span />
                              <span />
                            </div>
                            <div className="t05-product-stack__panel-title">{section.panelTitle}</div>
                            <div className="t05-product-stack__panel-rows">
                              {section.panelRows.map((row) => (
                                <div key={row.label} className="t05-product-stack__panel-row">
                                  <span>{row.label}</span>
                                  <span className="t05-product-stack__panel-badge">{row.badge}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      <img
                        src={iconSrc}
                        alt=""
                        className="t05-product-stack__body-icon"
                        width={140}
                        height={140}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
          <div data-stack-tail className="t05-product-stack__tail" aria-hidden />
        </div>
      </div>
    </section>
  );
};
